package com.neurobs.ccabserver.administrator.components

import com.neurobs.ccab.{CCABServer, ControllerSessionData, SessionConfig, StudySessionManager}
import com.neurobs.ccabserver.administrator.contexts.{GuiContext, LoggerContext, MenuContext, ServerContext, TestStationContext, UserContext}
import com.neurobs.ccabserver.administrator.controller.{ControllerSessionController, ExaminerTagList}
import com.neurobs.errors.Result
import com.neurobs.js.view.Gui
import com.neurobs.react.{Button, ButtonSize, CardContentProvider, Table, TableCell, TableHeader, TableRow, includeIf}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.{useEffect, useState}
import slinky.core.facade.{React, ReactElement}
import slinky.web.html.{div, span}

import scala.concurrent.ExecutionContext.Implicits.global
import scala.concurrent.Future

@react object TestStationListForm {
	case class Props
	(
		stations: Seq[ControllerSessionData],
		delegate: CardContentProvider.Delegate
	)

	val component = React.memo(
		FunctionalComponent[Props] { props =>
			val server = ServerContext.accessState()
			val controllers = TestStationContext.accessState()
			val user = UserContext.accessState()
			val gui = GuiContext.accessState()
			implicit val logger = LoggerContext.accessState()

			def connect(station: ControllerSessionData): Unit = {
				val userDataFut = server.getUserAccount(accountId = user.user.get.accountId, userId = user.user.get.userId)._2
				val studyTypesFut = server.getStudyTypes()._2
				val examinerTagsFut = server.getExaminerTags()._2
				val sessionConfigFut = server.getSessionConfigData()._2
				for (
					userDataRes <- userDataFut;
					studyTypesRes <- studyTypesFut;
					examinerTagsRes <- examinerTagsFut;
					sessionConfigRes <- sessionConfigFut
				) {
					val res = for (
						userData <- userDataRes;
						studyTypes <- studyTypesRes;
						examinerTags <- examinerTagsRes;
						sessionConfig <- sessionConfigRes
					) yield {
						val c = new ControllerSessionController(user = userData, studyTypes = studyTypes, gui = gui, sessionInfo = station, dataPassword = user.value.dataPasswords, examinerTags = new ExaminerTagList(examinerTags), server = server, studySessions = new StudySessionManager(sessionConfig))
						controllers.addExaminer(c)
					}
					res match {
						case Left(error) => props.delegate.replaceWithError(error.description)
						case _ =>
					}
				}
			}

			if (props.stations.isEmpty) span("There are no online Test Stations associated with your account.")
			else
				Table(
					TableHeader(
						TableCell(""),
						TableCell("Test Station"),
						TableCell("Computer Name"),
						TableCell("Created")
					),
					props.stations map { s =>
						val can_connect = !controllers.data.examiners.exists(_.sessionInfo.sessionId == s.sessionId)
						TableRow(
							TableCell(
								includeIf(can_connect)(
									Button(
										caption = "Connect",
										size = ButtonSize.Small,
										onClick = () => connect(s)
									)
								)
							),
							TableCell(s.controllerName),
							TableCell(s.computerName),
							TableCell(Gui.toLocalDateTime(s.createdAt))
						).withKey(key = s.sessionId)
					}
				)
		}
	)
}

object TestStationListCard extends CardContentProvider {
	override def title = "Online Test Stations"
	override def canClose = false
	override def refreshFunction: Option[CCABServer => Future[Result[AnyRef]]] = Some(server =>
		server.getControllerSessions()._2
	)
	override def content(d: CardContentProvider.Delegate, contentData: Option[AnyRef]): ReactElement = contentData match {
		case None => div()
		case Some(data) => TestStationListForm(stations = data.asInstanceOf[Seq[ControllerSessionData]], delegate = d)
	}
}
